/**
 * Shared utility for computing role acknowledgement status across employees.
 * Used by: RolesResponsibilitiesSettings (overview), EmployeesPage (badges/stats),
 * and the employee portal (Phase 4). Never the admin Action Centre: a person's
 * own role acknowledgements are an employee-portal item (EMP-B-003, ACEA-01).
 */

/**
 * Compute acknowledgement status for a single employee's role assignments.
 * @param {Object} employee
 * @param {Array} roleDefinitions - tenant-level role definitions
 * @returns {{ assigned: number, acknowledged: number, pending: number, reackNeeded: number, orphaned: number, items: Array }}
 */
import { realEmployees } from './sampleRecords.js';
export function getEmployeeRoleAckStatus(employee, roleDefinitions) {
  // GH #6269 (EMP-B-002): an unassigned-but-acknowledged entry is preserved
  // with an `unassignedAt` stamp rather than removed, so it must not count as
  // a live assignment here — it is evidence, not a current obligation.
  const assignments = (employee.assignedRoles || []).filter((a) => !a?.unassignedAt);
  let acknowledged = 0;
  let pending = 0;
  let reackNeeded = 0;
  let orphaned = 0;
  const items = [];

  for (const a of assignments) {
    const rd = roleDefinitions.find((r) => r.id === a.roleDefId);
    if (!rd) {
      orphaned++;
      items.push({ ...a, status: 'orphaned', definition: null });
      continue;
    }

    const currentVersion = rd.version || 1;
    if (a.acknowledgedAt == null) {
      pending++;
      items.push({ ...a, status: 'pending', definition: rd });
    } else if (a.acknowledgedVersion != null && a.acknowledgedVersion < currentVersion) {
      reackNeeded++;
      items.push({ ...a, status: 'reack-needed', definition: rd });
    } else {
      acknowledged++;
      items.push({ ...a, status: 'acknowledged', definition: rd });
    }
  }

  return {
    assigned: assignments.length,
    acknowledged,
    pending,
    reackNeeded,
    orphaned,
    items,
  };
}

/**
 * Compute aggregate acknowledgement stats across all employees.
 * @param {Array} employees
 * @param {Array} roleDefinitions
 * @returns {{ totalAssignments: number, acknowledged: number, pending: number, reackNeeded: number, employeesWithPending: Array, employeesWithReack: Array }}
 */
export function getAggregateRoleAckStatus(employees, roleDefinitions) {
  let totalAssignments = 0;
  let acknowledged = 0;
  let pending = 0;
  let reackNeeded = 0;
  const employeesWithPending = [];
  const employeesWithReack = [];

  for (const emp of realEmployees(employees || [])) {
    // GH #7870: sample people never count.
    const status = getEmployeeRoleAckStatus(emp, roleDefinitions);
    totalAssignments += status.assigned;
    acknowledged += status.acknowledged;
    pending += status.pending;
    reackNeeded += status.reackNeeded;

    if (status.pending > 0) {
      employeesWithPending.push({ employee: emp, count: status.pending });
    }
    if (status.reackNeeded > 0) {
      employeesWithReack.push({ employee: emp, count: status.reackNeeded });
    }
  }

  return {
    totalAssignments,
    acknowledged,
    pending,
    reackNeeded,
    employeesWithPending,
    employeesWithReack,
  };
}
